import React, { FC, useCallback } from 'react'
import { gql, useQuery } from '@apollo/client'
import { LinkButton } from '@/atoms/Button'
import { FlexRow } from '@/atoms/FlexContainers'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { CaptionSM } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { Avatar } from '@/molecules/Avatar'
import { useUser } from '@/services/UserService'
import { GetTotalEntriesQuery, GetTotalEntriesQueryVariables } from '@/types/codegen-federation'
import { useLoginUrl } from '@/utils/auth-utils'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { GEyebrowMD } from '@/views/PreSales/PreSalesGiveawayView/components/UI/Typography'
import { useGiveawayContext } from '../../context/GiveawayContext'

// Move to queries/giveaway.queries.ts for better organization
export const GET_TOTAL_ENTRIES = gql`
  query getTotalEntries($input: GetTheatricalPresalesSummaryInput!) {
    getTheatricalPresalesSummary(input: $input) {
      sweepstakesSummary {
        totalEntries
      }
    }
  }
`

// ==========================================
// Sub-components
// ==========================================

const GiveawayNavLogo: FC = () => {
  const { theatricalSlug } = useGiveawayContext()
  const logoColor = theatricalSlug === slugs.sketch ? 'sketch-pink' : 'white'

  return <AngelLogo color={logoColor} height={32} />
}

const EntriesBadge: FC = () => {
  const { theatricalSlug } = useGiveawayContext()
  const { user } = useUser()
  const { t } = useTranslate('tickets')

  const { data } = useQuery<GetTotalEntriesQuery, GetTotalEntriesQueryVariables>(GET_TOTAL_ENTRIES, {
    variables: { input: { projectSlug: theatricalSlug } },
    errorPolicy: 'all',
    skip: !user,
  })

  const totalEntries = data?.getTheatricalPresalesSummary?.sweepstakesSummary?.[0]?.totalEntries ?? 0

  if (!totalEntries || totalEntries <= 0) {
    return null
  }

  return (
    <a
      className="rounded-md bg-giveaway-secondary px-2 py-1"
      href={`/pre-sales/${theatricalSlug}/giveaway/summary`}
      target="_blank"
      rel="noopener noreferrer"
    >
      <GEyebrowMD color="giveaway-text-primary-light" className="uppercase">
        {totalEntries} {t('entries', 'Entries')}
      </GEyebrowMD>
    </a>
  )
}

const UserSection: FC = () => {
  const { theatricalSlug } = useGiveawayContext()
  const { user } = useUser()
  const { t } = useTranslate('tickets')
  const { loginUrl, trackLoginStarted } = useLoginUrl(`/pre-sales/${theatricalSlug}/giveaway`)

  const handleAccountClick = useCallback(() => {
    window?.open('/account?t=myTickets', '_blank', 'noopener,noreferrer')
  }, [])

  const handleLoginClick = useCallback(() => {
    trackLoginStarted('top-nav')
  }, [trackLoginStarted])

  if (user) {
    return (
      <div className="w-fit overflow-hidden rounded-full border-2 border-black bg-cover ring-2 ring-white">
        <Avatar onClick={handleAccountClick} />
      </div>
    )
  }

  return (
    <div className="flex items-center">
      <LinkButton
        href={loginUrl}
        variant="white"
        outline
        className="box-border h-8 rounded-md backdrop-blur-sm"
        onClick={handleLoginClick}
      >
        <CaptionSM className="whitespace-nowrap">
          <Translate t={t} i18nKey="logIn">
            Log In
          </Translate>
        </CaptionSM>
      </LinkButton>
    </div>
  )
}

export const GiveawayNav: FC = () => {
  return (
    <nav className="relative flex items-center justify-between">
      <GiveawayNavLogo />

      <FlexRow className="gap-4">
        <EntriesBadge />
        <UserSection />
      </FlexRow>
    </nav>
  )
}
